import type { Item, Stage } from '@mystiq/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { keys, useFolders, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { AiDetails, Close, ICON, Link as LinkIcon, Move, Restore, Site, Trash, Write } from '../icons.ts';
import { DebouncedSaver } from '../lib/debounced-saver.ts';
import { stageName, stageState, timeAgo } from '../lib/format.ts';
import { MoveDialog } from './MoveDialog.tsx';
import { ShareDialog } from './ShareDialog.tsx';
import { SiteDialog } from './SiteDialog.tsx';
import { TransformDialog } from './TransformDialog.tsx';
import { ErrorLine, iconForItem, Thumb } from './ui.tsx';

const canEdit = (item: Item) => item.permittedActions.includes('rename');

export function FolderCrumb({ ws, item }: { ws: string; item: Item }) {
  const folders = useFolders(ws);
  const folder = folders.data?.find((f) => f.id === item.folderId);
  return (
    <nav className="breadcrumbs" aria-label="Location">
      <Link to={`/w/${ws}/files`}>Files</Link>
      {folder && (
        <>
          <span aria-hidden>/</span>
          <Link to={`/w/${ws}/folders/${folder.id}`}>{folder.name}</Link>
        </>
      )}
    </nav>
  );
}

export function byline(item: Item): string {
  const who = item.createdBy.kind === 'agent' ? `an agent for ${item.createdBy.displayName ?? 'a member'}` : item.createdBy.kind === 'system' ? 'Mystiq' : (item.createdBy.displayName ?? 'a member');
  return `Created by ${who} · Edited ${timeAgo(item.updatedAt)}`;
}

/**
 * An autosaved metadata field (narrow PATCH, no Save button). Only the
 * person's typing is saved; values saved elsewhere (AI, an agent) replace
 * the field only while nothing typed is pending, and are never sent back.
 * Pending typing is flushed when the view closes.
 */
function useSavedField(saved: string, save: (value: string) => Promise<unknown>) {
  const [value, setValue] = useState(saved);
  const [error, setError] = useState<unknown>(null);
  const saver = useRef<DebouncedSaver<string> | null>(null);
  if (!saver.current) saver.current = new DebouncedSaver(saved, save, 800, { onError: setError, onSaved: () => setError(null) });
  useEffect(() => {
    if (saver.current!.hasPending) return;
    saver.current!.rebase(saved);
    setValue(saved);
  }, [saved]);
  useEffect(() => () => void saver.current!.flush(), []);
  const edit = (next: string) => {
    setValue(next);
    saver.current!.change(next);
  };
  return { value, edit, error };
}

/** Editable page title; becomes the item's deliberate (manual) title. */
export function TitleField({ ws, item, placeholder }: { ws: string; item: Item; placeholder: string }) {
  const api = useApi();
  const title = useSavedField(item.title, (value) => api.updateItem(ws, item.id, { title: value }));
  return (
    <>
      <input
        className="title-input"
        aria-label="Title"
        value={title.value}
        placeholder={placeholder}
        readOnly={!canEdit(item)}
        onChange={(event) => title.edit(event.target.value)}
      />
      <ErrorLine error={title.error} />
    </>
  );
}

export function Details({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const editable = canEdit(item);
  const description = useSavedField(item.description ?? '', (value) => api.updateItem(ws, item.id, { description: value.trim() ? value : null }));
  const [newTag, setNewTag] = useState('');
  const tags = useWorkspaceMutation(ws, (change: { addTags?: string[]; removeTags?: string[] }) => api.updateItem(ws, item.id, change));

  return (
    <section className="section" aria-labelledby="details-heading">
      <h2 className="section-heading" id="details-heading">Details</h2>
      <div className="stack">
        <label className="field">
          <span className="field-label">Description</span>
          <textarea
            className="textarea"
            aria-label="Description"
            value={description.value}
            readOnly={!editable}
            placeholder={editable ? 'What is this about?' : ''}
            onChange={(event) => description.edit(event.target.value)}
          />
          {item.descriptionOrigin === 'system' && (
            <span className="meta">
              <AiDetails size={ICON.inline} stroke={ICON.stroke} /> Written by AI. Edit it to make it yours; AI will not change it afterwards.
            </span>
          )}
          <ErrorLine error={description.error} />
        </label>
        <div className="field">
          <span className="field-label">Tags</span>
          <div className="chips">
            {item.tagDetails.map(({ tag, origin }) => (
              <span className="chip" key={tag}>
                {origin === 'system' && <AiDetails size={ICON.inline} stroke={ICON.stroke} aria-label="Suggested by AI" />}
                {tag}
                {editable && (
                  <button aria-label={`Remove tag ${tag}`} onClick={() => tags.mutate({ removeTags: [tag] })}>
                    <Close size={ICON.inline} stroke={ICON.stroke} />
                  </button>
                )}
              </span>
            ))}
            {!item.tagDetails.length && <span className="meta">No tags yet.</span>}
          </div>
          {editable && (
            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (newTag.trim()) tags.mutate({ addTags: [newTag.trim()] });
                setNewTag('');
              }}
            >
              <input className="input" aria-label="New tag" placeholder="Add a tag" value={newTag} onChange={(event) => setNewTag(event.target.value)} />
              <button className="button" type="submit">
                Add tag
              </button>
            </form>
          )}
          <ErrorLine error={tags.error} />
        </div>
      </div>
    </section>
  );
}

/** Steps a person can run again from the item; a written note is a new request instead. */
const RUN_AGAIN: Partial<Record<Stage['kind'], string>> = { extract_text: 'Read again', transcribe: 'Transcribe again', enrich_metadata: 'Regenerate' };

function StageLine({ ws, itemId, stage, canChange }: { ws: string; itemId: string; stage: Stage; canChange: boolean }) {
  const api = useApi();
  const retry = useWorkspaceMutation(ws, () => api.retryOperation(ws, stage.operationId));
  const runAgain = RUN_AGAIN[stage.kind];
  const again = useWorkspaceMutation(ws, () => api.reprocess(ws, itemId, stage.kind));
  const failed = stage.status === 'failed' || stage.status === 'cancelled';
  // Unsupported stays unsupported for the same bytes, so it offers nothing to run again.
  const done = stage.status === 'succeeded' || stage.status === 'partial';
  return (
    <li className="row">
      <Thumb icon={AiDetails} />
      <span className="row-body">
        <span className="row-title">{stageName(stage)}</span>
        <span className="row-meta">
          <span className="state">{stageState(stage)}.</span> {stage.errorMessage ?? ''}
          {stage.attempts > 1 ? ` (${stage.attempts} attempts)` : ''}
        </span>
        {stage.notes.map((note) => (
          <span className="row-meta" key={note}>
            {note}
          </span>
        ))}
        <ErrorLine error={again.error ?? retry.error} />
      </span>
      {failed && canChange && (
        <button className="link-button" disabled={retry.isPending} onClick={() => retry.mutate(undefined)}>
          Retry
        </button>
      )}
      {done && (stage.kind === 'transform' || stage.kind === 'generate_site') && stage.outputItemId && stage.outputItemId !== itemId && (
        <Link className="link-button" to={`/w/${ws}/items/${stage.outputItemId}`}>
          Open result
        </Link>
      )}
      {done && canChange && runAgain && (
        <button className="link-button" disabled={again.isPending} onClick={() => again.mutate(undefined)}>
          {runAgain}
        </button>
      )}
    </li>
  );
}

export function Processing({ ws, item }: { ws: string; item: Item }) {
  if (!item.stages.length) return null;
  return (
    <section className="section" aria-labelledby="processing-heading">
      <h2 className="section-heading" id="processing-heading">Processing</h2>
      <ul className="rows">
        {item.stages.map((stage) => (
          <StageLine key={stage.operationId} ws={ws} itemId={item.id} stage={stage} canChange={canEdit(item)} />
        ))}
      </ul>
    </section>
  );
}

type RelatedData = Awaited<ReturnType<ReturnType<typeof useApi>['related']>>;

function RelatedGroup({ ws, data, label }: { ws: string; data: RelatedData | undefined; label: string }) {
  const nodes = data?.nodes ?? [];
  if (!nodes.length) return null;
  return (
    <div className="stack">
      <p className="field-label">{label}</p>
      <ul className="rows">
        {nodes.map((node) => {
          const status = node.available ? (node.depth > 1 ? `${node.depth} steps away` : 'Available') : node.lifecycle === 'trashed' ? 'In Trash' : 'Permanently deleted';
          const body = (
            <>
              <Thumb icon={iconForItem(node)} />
              <span className="row-body">
                <span className="row-title">{node.title?.trim() ? node.title : node.title === null ? 'Deleted item' : 'Untitled'}</span>
                <span className="row-meta">{node.available ? status : <span className="state">{status}.</span>}</span>
              </span>
            </>
          );
          return (
            <li key={`${node.itemId}-${node.depth}`}>
              {node.title !== null ? (
                <Link className="row" to={`/w/${ws}/items/${node.itemId}`}>
                  {body}
                </Link>
              ) : (
                <div className="row">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Source and output navigation; hidden when the item has no relationships. */
export function RelatedSection({ ws, itemId }: { ws: string; itemId: string }) {
  const api = useApi();
  const sources = useQuery({ queryKey: keys.related(ws, itemId, 'sources'), queryFn: () => api.related(ws, itemId, 'sources') });
  const derivatives = useQuery({ queryKey: keys.related(ws, itemId, 'derivatives'), queryFn: () => api.related(ws, itemId, 'derivatives') });
  if (!sources.data?.nodes.length && !derivatives.data?.nodes.length) return null;
  return (
    <section className="section" aria-labelledby="related-heading">
      <h2 className="section-heading" id="related-heading">Related</h2>
      <div className="stack">
        <RelatedGroup ws={ws} data={sources.data} label="Made from" />
        <RelatedGroup ws={ws} data={derivatives.data} label="Used to make" />
        <p className="meta">Outputs keep working on their own: moving a source to Trash leaves them in place.</p>
      </div>
    </section>
  );
}

/** Notes, text items and transcripts always have text; a file has text once it has been read or transcribed. */
function hasText(item: Item): boolean {
  if (item.kind !== 'file') return true;
  const stage = item.stages.find((s) => s.kind === 'extract_text' || s.kind === 'transcribe');
  return Boolean(stage && (stage.status === 'succeeded' || stage.status === 'partial') && stage.outputItemId);
}

export function ItemActions({ ws, item, shareRevision }: { ws: string; item: Item; shareRevision?: { revisionId: string; label: string } | undefined }) {
  const api = useApi();
  const navigate = useNavigate();
  const [moving, setMoving] = useState(false);
  const [writing, setWriting] = useState(false);
  const [makingSite, setMakingSite] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [requested, setRequested] = useState<'note' | 'site' | null>(null);
  const move = useWorkspaceMutation(ws, (folderId: string) => api.moveItem(ws, item.id, folderId));
  const trash = useWorkspaceMutation(ws, () => api.trashItem(ws, item.id));
  if (!canEdit(item)) return null;
  return (
    <div className="toolbar">
      {hasText(item) && (
        <button className="button" onClick={() => setWriting(true)}>
          <Write size={ICON.nav} stroke={ICON.stroke} />
          Write with AI
        </button>
      )}
      {hasText(item) && item.kind !== 'site' && (
        <button className="button" onClick={() => setMakingSite(true)}>
          <Site size={ICON.nav} stroke={ICON.stroke} />
          Make a Site
        </button>
      )}
      {(item.kind !== 'file' || item.storageState === 'available') && (
        <button className="button" onClick={() => setSharing(true)}>
          <LinkIcon size={ICON.nav} stroke={ICON.stroke} />
          Share
        </button>
      )}
      <button className="button" onClick={() => setMoving(true)}>
        <Move size={ICON.nav} stroke={ICON.stroke} />
        Move
      </button>
      <button
        className="button"
        onClick={async () => {
          if (await trash.mutateAsync(undefined).then(() => true, () => false)) navigate(`/w/${ws}/folders/${item.folderId}`);
        }}
      >
        <Trash size={ICON.nav} stroke={ICON.stroke} />
        Move to Trash
      </button>
      <ErrorLine error={trash.error} />
      {requested && (
        <p className="meta" role="status">
          {requested === 'site' ? (
            <>
              <span className="state">Making a Site.</span> It appears under Processing when it is ready.
            </>
          ) : (
            <>
              <span className="state">Writing.</span> The new note appears under Processing when it is ready.
            </>
          )}
        </p>
      )}
      {moving && <MoveDialog ws={ws} currentFolderId={item.folderId} onMove={(folderId) => move.mutateAsync(folderId)} onClose={() => setMoving(false)} />}
      {sharing && <ShareDialog ws={ws} item={item} revision={shareRevision} onClose={() => setSharing(false)} />}
      {makingSite && <SiteDialog ws={ws} sourceItemIds={[item.id]} onClose={() => setMakingSite(false)} onRequested={() => setRequested('site')} />}
      {writing && (
        <TransformDialog
          ws={ws}
          sourceItemIds={[item.id]}
          speech={item.kind === 'transcript' || item.capture !== null}
          onClose={() => setWriting(false)}
          onRequested={() => setRequested('note')}
        />
      )}
    </div>
  );
}

export function TrashedNotice({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const restore = useWorkspaceMutation(ws, () => api.restoreItem(ws, item.id));
  return (
    <div className="page">
      <h1 className="page-title">{item.displayTitle}</h1>
      <div className="content stack">
        <p>
          <span className="state">In Trash.</span> Its outputs stay available. Restore it to open, edit or download it again.
        </p>
        {item.permittedActions.includes('restore') && (
          <div>
            <button className="button button-primary" onClick={() => restore.mutate(undefined)} disabled={restore.isPending}>
              <Restore size={ICON.nav} stroke={ICON.stroke} />
              Restore
            </button>
          </div>
        )}
        <ErrorLine error={restore.error} />
      </div>
      <RelatedSection ws={ws} itemId={item.id} />
    </div>
  );
}
